// Pure handling of the API's 402 plan-limit contract: recognise the body, turn
// it into a typed error the hooks can throw, and render the one consistent
// message the UI shows. No React, no I/O.

import type { PlanLimitPayload, LimitedResource } from '@/types/account';

// Where "talk to us" goes. The marketing site's contact page lives on the same
// origin as the app.
export const CONTACT_PATH = '/contact';

const RESOURCES: ReadonlySet<string> = new Set<LimitedResource>([
  'access',
  'projects',
  'conversations',
  'researchRuns',
  'dialogs',
]);

export class PlanLimitError extends Error {
  constructor(readonly payload: PlanLimitPayload) {
    super(planLimitMessage(payload).description);
    this.name = 'PlanLimitError';
  }
}

export function parsePlanLimit(data: unknown): PlanLimitPayload | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (d.code !== 'plan_limit') return null;
  if (typeof d.resource !== 'string' || !RESOURCES.has(d.resource)) return null;
  return {
    code: 'plan_limit',
    resource: d.resource as LimitedResource,
    limit: typeof d.limit === 'number' ? d.limit : null,
    used: typeof d.used === 'number' ? d.used : 0,
    remaining: typeof d.remaining === 'number' ? d.remaining : 0,
    plan: d.plan === 'base' || d.plan === 'internal' ? d.plan : null,
  };
}

// The API layer returns {success:false, data, message} rather than throwing.
// Hooks call this where they would otherwise `throw new Error(message)`, so a
// 402 surfaces as a PlanLimitError and everything else stays as it was.
export function toApiError(response: { success: boolean; data: unknown; message?: string }, fallback: string): Error {
  const payload = parsePlanLimit(response.data);
  if (payload) return new PlanLimitError(payload);
  return new Error(response.message || fallback);
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export function planLimitMessage(p: PlanLimitPayload): { title: string; description: string } {
  const limit = p.limit ?? 0;
  switch (p.resource) {
    case 'access':
      return {
        title: 'ValueGraph is invite-only right now',
        description: "Your account isn't set up for this yet. Ask us for access and we'll sort it out.",
      };
    case 'projects':
      return {
        title: "You've used your project allowance",
        description: `Your plan includes ${plural(limit, 'project', 'projects')}. Deleting one doesn't free the space. Talk to us if you need more.`,
      };
    case 'conversations':
      return {
        title: 'This project has used its conversations',
        description: `Your plan includes ${plural(limit, 'customer conversation', 'customer conversations')} for this project. Talk to us if you need more.`,
      };
    case 'researchRuns':
      return {
        title: `This project has used its research ${limit === 1 ? 'run' : 'runs'}`,
        description: `Your plan includes ${plural(limit, 'research run', 'research runs')} for this project. Talk to us if you need another.`,
      };
    case 'dialogs':
      return {
        title: 'This project has used its open dialogs',
        description: `Your plan includes ${plural(limit, 'open dialog', 'open dialogs')} at once for this project. Closing one frees a slot so you can open another.`,
      };
  }
}

// "2 of 3 conversations used", or null when there is nothing to show
// (unlimited plan, or a response that predates limits).
export function allowanceLabel(
  used: number,
  limit: number | null | undefined,
  singular: string,
  plural: string,
): string | null {
  if (limit === null || limit === undefined) return null;
  return `${used} of ${limit} ${limit === 1 ? singular : plural} used`;
}
